import React, { useState } from 'react';
import { 
  Eye, Download, Maximize2, Minimize2, Smartphone, 
  Monitor, Tablet, RotateCcw, ZoomIn, ZoomOut,
  Grid3x3, Type, Image, Palette, CheckCircle
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';

interface FlyerData {
  id: string;
  title: string;
  size: { width: number; height: number; unit: string };
  elements: FlyerElement[];
  background: string;
  theme: string;
  version: string;
  status: 'draft' | 'review' | 'approved';
  lastModified: Date;
}

interface FlyerElement {
  id: string;
  type: 'text' | 'image' | 'shape' | 'barcode' | 'qrcode';
  content: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  style: {
    fontSize?: string;
    fontWeight?: string;
    color?: string;
    backgroundColor?: string;
    borderRadius?: string;
    opacity?: number;
  };
  layer: number;
}

interface FlyerPreviewProps {
  flyerData?: FlyerData;
  onExport?: (format: string) => void;
  onApprove?: () => void;
}

const FlyerPreview: React.FC<FlyerPreviewProps> = ({ 
  flyerData, 
  onExport, 
  onApprove 
}) => {
  const [viewMode, setViewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [zoom, setZoom] = useState(100);
  const [showGrid, setShowGrid] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // サンプルデータ
  const sampleFlyer: FlyerData = flyerData || {
    id: 'flyer-001',
    title: '春の新生活フェア チラシ',
    size: { width: 210, height: 297, unit: 'mm' },
    elements: [
      {
        id: 'header',
        type: 'text',
        content: '春の新生活フェア',
        position: { x: 10, y: 10 },
        size: { width: 190, height: 40 },
        style: { fontSize: '32px', fontWeight: 'bold', color: colors.accent.red },
        layer: 3
      },
      {
        id: 'subtitle',
        type: 'text',
        content: '最大50%OFF! 3月31日まで',
        position: { x: 10, y: 55 },
        size: { width: 190, height: 20 },
        style: { fontSize: '18px', color: colors.text.secondary },
        layer: 2
      },
      {
        id: 'product1',
        type: 'image',
        content: '/api/placeholder/150/150',
        position: { x: 20, y: 90 },
        size: { width: 80, height: 80 },
        style: { borderRadius: '8px' },
        layer: 1
      },
      {
        id: 'price1',
        type: 'text',
        content: '¥9,800',
        position: { x: 110, y: 120 },
        size: { width: 80, height: 30 },
        style: { fontSize: '24px', fontWeight: 'bold', color: colors.accent.red },
        layer: 2
      }
    ],
    background: '#FFFFFF',
    theme: 'spring',
    version: '1.2.0',
    status: 'review',
    lastModified: new Date()
  };

  const currentFlyer = flyerData || sampleFlyer;

  // ズーム処理
  const handleZoomIn = () => {
    if (zoom < 200) setZoom(zoom + 10);
  };

  const handleZoomOut = () => {
    if (zoom > 50) setZoom(zoom - 10);
  };

  const handleZoomReset = () => {
    setZoom(100);
  };

  // エクスポート処理
  const handleExport = (format: string) => {
    if (onExport) {
      onExport(format);
    } else {
      console.log(`Exporting as ${format}...`);
      // ここに実際のエクスポート処理を実装
    }
  };

  // 承認処理
  const handleApprove = () => {
    if (onApprove) {
      onApprove();
    } else {
      console.log('Approving flyer...');
      // ここに実際の承認処理を実装
    }
  };

  // ビューポートサイズの取得
  const getViewportSize = () => {
    switch (viewMode) {
      case 'mobile':
        return { width: 375, height: 667 };
      case 'tablet':
        return { width: 768, height: 1024 };
      default:
        return { width: 1366, height: 768 };
    }
  };

  return (
    <div className="space-y-6">
      {/* ツールバー */}
      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <h3 className="font-semibold" style={{ color: colors.text.primary }}>
                {currentFlyer.title}
              </h3>
              <Badge 
                variant={currentFlyer.status === 'approved' ? 'success' : 
                        currentFlyer.status === 'review' ? 'warning' : 'default'}
              >
                {currentFlyer.status === 'draft' ? '下書き' :
                 currentFlyer.status === 'review' ? 'レビュー中' : '承認済み'}
              </Badge>
            </div>
            <div className="flex items-center space-x-2">
              {/* デバイスビュー切り替え */}
              <div className="flex items-center space-x-1 border rounded-lg p-1" 
                   style={{ borderColor: colors.ui.border }}>
                <button
                  onClick={() => setViewMode('desktop')}
                  className={`p-1.5 rounded ${viewMode === 'desktop' ? 'bg-gray-100' : ''}`}
                  title="デスクトップビュー"
                >
                  <Monitor size={16} style={{ 
                    color: viewMode === 'desktop' ? colors.accent.blue : colors.text.secondary 
                  }} />
                </button>
                <button
                  onClick={() => setViewMode('tablet')}
                  className={`p-1.5 rounded ${viewMode === 'tablet' ? 'bg-gray-100' : ''}`}
                  title="タブレットビュー"
                >
                  <Tablet size={16} style={{ 
                    color: viewMode === 'tablet' ? colors.accent.blue : colors.text.secondary 
                  }} />
                </button>
                <button
                  onClick={() => setViewMode('mobile')}
                  className={`p-1.5 rounded ${viewMode === 'mobile' ? 'bg-gray-100' : ''}`}
                  title="モバイルビュー"
                >
                  <Smartphone size={16} style={{ 
                    color: viewMode === 'mobile' ? colors.accent.blue : colors.text.secondary 
                  }} />
                </button>
              </div>

              {/* ズームコントロール */}
              <div className="flex items-center space-x-1 border rounded-lg p-1"
                   style={{ borderColor: colors.ui.border }}>
                <button
                  onClick={handleZoomOut}
                  className="p-1.5 rounded hover:bg-gray-100"
                  title="ズームアウト"
                >
                  <ZoomOut size={16} style={{ color: colors.text.secondary }} />
                </button>
                <span className="px-2 text-sm" style={{ color: colors.text.secondary }}>
                  {zoom}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-1.5 rounded hover:bg-gray-100"
                  title="ズームイン"
                >
                  <ZoomIn size={16} style={{ color: colors.text.secondary }} />
                </button>
                <button
                  onClick={handleZoomReset}
                  className="p-1.5 rounded hover:bg-gray-100"
                  title="ズームリセット"
                >
                  <RotateCcw size={16} style={{ color: colors.text.secondary }} />
                </button>
              </div>

              {/* グリッド表示 */}
              <button
                onClick={() => setShowGrid(!showGrid)}
                className={`p-2 rounded ${showGrid ? 'bg-gray-100' : ''}`}
                title="グリッド表示"
              >
                <Grid3x3 size={16} style={{ 
                  color: showGrid ? colors.accent.blue : colors.text.secondary 
                }} />
              </button>

              {/* フルスクリーン */}
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="p-2 rounded hover:bg-gray-100"
                title={isFullscreen ? "フルスクリーン終了" : "フルスクリーン"}
              >
                {isFullscreen ? (
                  <Minimize2 size={16} style={{ color: colors.text.secondary }} />
                ) : (
                  <Maximize2 size={16} style={{ color: colors.text.secondary }} />
                )}
              </button>
            </div>
          </div>

          {/* メタ情報 */}
          <div className="flex items-center space-x-4 text-sm" style={{ color: colors.text.secondary }}>
            <span>サイズ: {currentFlyer.size.width}×{currentFlyer.size.height}{currentFlyer.size.unit}</span>
            <span>要素数: {currentFlyer.elements.length}</span>
            <span>バージョン: {currentFlyer.version}</span>
            <span>最終更新: {currentFlyer.lastModified.toLocaleString('ja-JP')}</span>
          </div>
        </div>
      </Card>

      {/* プレビューエリア */}
      <Card>
        <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-white' : ''} p-6`}>
          <div className="bg-gray-50 rounded-lg p-8 overflow-auto" 
               style={{ maxHeight: isFullscreen ? '100vh' : '600px' }}>
            <div 
              className="mx-auto bg-white shadow-lg relative"
              style={{
                width: `${currentFlyer.size.width * 3}px`,
                height: `${currentFlyer.size.height * 3}px`,
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top center',
                backgroundColor: currentFlyer.background,
                backgroundImage: showGrid ? 
                  'repeating-linear-gradient(0deg, #f0f0f0 0px, transparent 1px, transparent 19px, #f0f0f0 20px), repeating-linear-gradient(90deg, #f0f0f0 0px, transparent 1px, transparent 19px, #f0f0f0 20px)' :
                  'none'
              }}
            >
              {/* フライヤー要素のレンダリング */}
              {currentFlyer.elements
                .sort((a, b) => a.layer - b.layer)
                .map(element => (
                  <div
                    key={element.id}
                    className="absolute"
                    style={{
                      left: `${element.position.x}%`,
                      top: `${element.position.y}%`,
                      width: `${element.size.width}%`,
                      height: `${element.size.height}%`,
                      ...element.style
                    }}
                  >
                    {element.type === 'text' && (
                      <div style={element.style}>{element.content}</div>
                    )}
                    {element.type === 'image' && (
                      <img 
                        src={element.content} 
                        alt="" 
                        className="w-full h-full object-cover"
                        style={{ borderRadius: element.style.borderRadius }}
                      />
                    )}
                    {element.type === 'shape' && (
                      <div 
                        className="w-full h-full"
                        style={{
                          backgroundColor: element.style.backgroundColor,
                          borderRadius: element.style.borderRadius
                        }}
                      />
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </Card>

      {/* アクションパネル */}
      <Card>
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {/* レイヤー情報 */}
            <button className="flex items-center space-x-2 px-3 py-2 border rounded-lg hover:bg-gray-50"
                    style={{ borderColor: colors.ui.border }}>
              <Type size={16} style={{ color: colors.text.secondary }} />
              <span className="text-sm" style={{ color: colors.text.secondary }}>
                テキスト: {currentFlyer.elements.filter(e => e.type === 'text').length}
              </span>
            </button>
            <button className="flex items-center space-x-2 px-3 py-2 border rounded-lg hover:bg-gray-50"
                    style={{ borderColor: colors.ui.border }}>
              <Image size={16} style={{ color: colors.text.secondary }} />
              <span className="text-sm" style={{ color: colors.text.secondary }}>
                画像: {currentFlyer.elements.filter(e => e.type === 'image').length}
              </span>
            </button>
            <button className="flex items-center space-x-2 px-3 py-2 border rounded-lg hover:bg-gray-50"
                    style={{ borderColor: colors.ui.border }}>
              <Palette size={16} style={{ color: colors.text.secondary }} />
              <span className="text-sm" style={{ color: colors.text.secondary }}>
                テーマ: {currentFlyer.theme}
              </span>
            </button>
          </div>

          <div className="flex items-center space-x-2">
            {/* エクスポートオプション */}
            <div className="flex items-center space-x-1 border rounded-lg p-1"
                 style={{ borderColor: colors.ui.border }}>
              <button
                onClick={() => handleExport('pdf')}
                className="px-3 py-1.5 text-sm hover:bg-gray-100 rounded"
                style={{ color: colors.text.secondary }}
              >
                PDF
              </button>
              <button
                onClick={() => handleExport('jpg')}
                className="px-3 py-1.5 text-sm hover:bg-gray-100 rounded"
                style={{ color: colors.text.secondary }}
              >
                JPG
              </button>
              <button
                onClick={() => handleExport('png')}
                className="px-3 py-1.5 text-sm hover:bg-gray-100 rounded"
                style={{ color: colors.text.secondary }}
              >
                PNG
              </button>
            </div>

            {/* メインアクション */}
            <Button variant="outline">
              <Download size={16} />
              <span>ダウンロード</span>
            </Button>
            {currentFlyer.status === 'review' && (
              <Button 
                variant="primary" 
                onClick={handleApprove}
              >
                <CheckCircle size={16} />
                <span>承認する</span>
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* プレビュー情報 */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <div className="p-4">
            <h4 className="text-sm font-medium mb-3" style={{ color: colors.text.secondary }}>
              カラーパレット
            </h4>
            <div className="flex space-x-2">
              {['#E85C56', '#4D8FEA', '#5CC8A1', '#F5A623'].map(color => (
                <div
                  key={color}
                  className="w-8 h-8 rounded"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <h4 className="text-sm font-medium mb-3" style={{ color: colors.text.secondary }}>
              使用フォント
            </h4>
            <div className="space-y-1">
              <div className="text-sm" style={{ color: colors.text.primary }}>
                Noto Sans JP
              </div>
              <div className="text-sm" style={{ color: colors.text.primary }}>
                Yu Gothic
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <h4 className="text-sm font-medium mb-3" style={{ color: colors.text.secondary }}>
              ファイル情報
            </h4>
            <div className="space-y-1">
              <div className="text-sm" style={{ color: colors.text.primary }}>
                解像度: 300dpi
              </div>
              <div className="text-sm" style={{ color: colors.text.primary }}>
                カラーモード: CMYK
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default FlyerPreview;
